/** /companies/:id: one company and every case it sends the firm. */
import { ArrowLeft, Building2, ChevronRight, Pencil } from "lucide-react";
import { useState, type FC } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { MetaLine } from "@/components/ui/meta-line";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tag } from "@/components/ui/tag";
import { useOnScreen } from "@/features/agents/onScreen";
import { usePracticeTeamMembers } from "@/hooks/usePracticeTeamMembers";

import { CompanyMark } from "../components/CompanyMark";
import { CompanyProfileSheet } from "../components/CompanyProfileSheet";
import { useCompany, useCompanyCases } from "../queries";
import { asCompanyTab, COMPANIES_PATH, type CompanyTab } from "../routes";
import { BillingTab } from "../tabs/BillingTab";
import { ContactsTab } from "../tabs/ContactsTab";
import { EmployeesTab } from "../tabs/EmployeesTab";
import { OverviewTab } from "../tabs/OverviewTab";
import { PlaybookTab } from "../tabs/PlaybookTab";
import { formatDate } from "../utils";

export const CompanyPage: FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const [params, setParams] = useSearchParams();
  const company = useCompany(companyId);
  const cases = useCompanyCases(companyId);
  const team = usePracticeTeamMembers();
  const [editing, setEditing] = useState(false);
  const [editField, setEditField] = useState<string>();
  const edit = (field?: string) => {
    setEditField(field);
    setEditing(true);
  };
  const tab = asCompanyTab(params.get("tab"));
  const ruleNumber = tab === "playbook" ? Number(params.get("rule") ?? 0) : 0;
  const rule = ruleNumber
    ? company.data?.playbook?.find((r) => r.number === ruleNumber)
    : undefined;
  useOnScreen(
    company.data
      ? rule
        ? {
            kind: "rule",
            id: String(rule.number),
            label: `${company.data.name} ${rule.number}`,
            company_id: company.data.id
          }
        : {
            kind: "company",
            id: company.data.id,
            label: company.data.name,
            company_id: company.data.id
          }
      : null
  );

  const setTab = (next: CompanyTab) =>
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        if (next === "overview") out.delete("tab");
        else out.set("tab", next);
        return out;
      },
      { replace: true }
    );

  if (company.isLoading) return <Skeleton className="m-6 h-40" />;
  if (!company.data) {
    return (
      <EmptyState
        icon={Building2}
        title="Company Not Found"
        description="It may have been removed."
        action={
          <Button asChild variant="secondary" size="sm">
            <Link to={COMPANIES_PATH}>Back to Companies</Link>
          </Button>
        }
        className="h-full"
      />
    );
  }
  const c = company.data;
  const rows = cases.data ?? [];
  const open = rows.filter((r) => r.open && !r.dependent_of).length;
  const rulesOn = (c.playbook ?? []).filter(
    (r) => r.status === "active" && r.enabled
  ).length;
  const lead = team.options.find((o) => o.uid === c.responsible_user_id)?.label;
  const sub = [
    c.client_no && `Client no. ${c.client_no}`,
    c.created_at && `Client since ${formatDate(c.created_at)}`,
    cases.data ? `${open} open ${open === 1 ? "case" : "cases"}` : null,
    lead
  ];

  return (
    <div
      className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-surface"
      data-testid="company-page"
    >
      <div className="flex h-10 shrink-0 items-center gap-1 border-b border-line-2 pr-4 pl-3">
        <Button
          asChild
          size="icon-sm"
          variant="ghost"
          aria-label="Back to Companies"
        >
          <Link to={COMPANIES_PATH}>
            <ArrowLeft />
          </Link>
        </Button>
        <nav
          aria-label="Breadcrumb"
          className="flex min-w-0 flex-1 items-center gap-1 text-base"
        >
          <Link
            to={COMPANIES_PATH}
            className="shrink-0 text-fg-3 transition-colors hover:text-fg"
          >
            Companies
          </Link>
          <ChevronRight className="size-3.5 shrink-0 text-fg-4" />
          <span className="truncate text-fg">{c.name}</span>
        </nav>
        <Button size="sm" variant="ghost" onClick={() => edit()}>
          <Pencil /> Edit Company
        </Button>
      </div>

      <div className="flex shrink-0 flex-col gap-3 px-8 pt-5">
        <CompanyMark name={c.name} className="size-10 text-base" />
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="flex min-w-0 items-center gap-2.5">
            <h1 className="truncate text-2xl font-medium text-fg">{c.name}</h1>
            <Tag>Company</Tag>
          </span>
          <MetaLine parts={sub} className="text-md" />
        </div>
      </div>

      <Tabs
        value={tab}
        onValueChange={(v) => setTab(asCompanyTab(v))}
        className="min-h-0 flex-1 gap-0"
      >
        <div className="flex shrink-0 items-center border-b border-line-2 px-6.5 pt-4 pb-3">
          <TabsList variant="pane">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="employees">
              Employees and Cases
              {cases.data ? (
                <span className="text-fg-4 tabular-nums">{rows.length}</span>
              ) : null}
            </TabsTrigger>
            <TabsTrigger value="playbook">
              Playbook
              <span className="text-fg-4 tabular-nums">{rulesOn}</span>
            </TabsTrigger>
            <TabsTrigger value="contacts">
              Contacts and Portal
              <span className="text-fg-4 tabular-nums">
                {c.contacts.length}
              </span>
            </TabsTrigger>
            <TabsTrigger value="billing">Billing</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="overview" className="flex min-h-0 flex-col">
          <OverviewTab company={c} cases={rows} onEdit={edit} />
        </TabsContent>
        <TabsContent value="employees" className="flex min-h-0 flex-col">
          <EmployeesTab
            company={c}
            cases={cases.data}
            loading={cases.isLoading}
          />
        </TabsContent>
        <TabsContent value="playbook" className="flex min-h-0 flex-col">
          <PlaybookTab company={c} cases={rows} />
        </TabsContent>
        <TabsContent value="contacts" className="flex min-h-0 flex-col">
          <ContactsTab company={c} />
        </TabsContent>
        <TabsContent value="billing" className="flex min-h-0 flex-col">
          <BillingTab company={c} />
        </TabsContent>
      </Tabs>

      <CompanyProfileSheet
        company={c}
        open={editing}
        onOpenChange={setEditing}
        field={editField}
      />
    </div>
  );
};
